// OPA Bundles page (roadmap item 45).
//
// Mounted at `/repos/:id/opa` for any repository whose `format` is `opa`.
// Surfaces the catalog of (bundle, version) pairs the repo holds and, for
// the bundle the operator clicks, four tabs:
//
//   * Overview  — revision, roots, file counts, sizes, pull URL
//   * Rego files — tree-style list with click-to-render of each `.rego`
//   * Data       — `data.json` documents discovered inside the bundle
//   * Manifest   — raw `.manifest` JSON
//
// Slug derivation matches the Kustomize / Ollama pages: `useRepoDetail`
// is UUID-keyed; the format API surface is slug-keyed; we look up the
// project slug from the cached project list.

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  Copy,
  Database,
  FileCode,
  FileText,
  Package,
  ScrollText,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { CopyIconButton } from '../../components/ui/CopyIconButton';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useProjects, useRepoDetail } from '../../api/client';
import {
  type OPABundleSummary,
  type OPARegoFile,
  type OPADataDoc,
  useOPABundleDetail,
  useOPABundles,
} from '../../api/opa';

function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function shortHex(d?: string): string {
  if (!d) return '';
  if (d.length <= 19) return d;
  return d.slice(0, 12) + '…' + d.slice(-4);
}

function bundlesCountLabel(list?: { bundles: unknown[] }): string | undefined {
  if (!list) return undefined;
  return `${list.bundles.length} bundle${list.bundles.length === 1 ? '' : 's'}`;
}

type TabId = 'overview' | 'rego' | 'data' | 'manifest';

export function OPABundles() {
  const { id } = useParams<{ id: string }>();
  const { data: repo, isLoading: repoLoading, error: repoError } = useRepoDetail(id);
  const { data: projects } = useProjects();
  const projectSlug = useMemo(
    () => projects?.find((p) => p.id === repo?.projectId)?.slug,
    [projects, repo?.projectId],
  );
  const { data: list, error: listError, isLoading: listLoading } = useOPABundles(
    projectSlug,
    repo?.slug,
  );
  const [selected, setSelected] = useState<OPABundleSummary | null>(null);

  if (repoLoading) {
    return <div className="p-6 text-fg-muted">Loading repository…</div>;
  }
  if (repoError || !repo) {
    return (
      <div className="p-6 text-danger">
        Failed to load repository: {String(repoError ?? 'unknown')}
      </div>
    );
  }
  if (repo.format !== 'opa') {
    return (
      <div className="p-6">
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to repository
        </Link>
        <p className="text-fg-muted">
          This page is only available for repositories of format{' '}
          <code className="orb-code">opa</code>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {repo.name}
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">OPA bundles</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          {repo.name}
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">
          OPA policy bundles in this repository. Each row shows the bundle
          name, version, manifest revision, and the file/root counts parsed
          from the archive. Click a row to inspect the Rego sources, data
          documents, and raw <code className="orb-code">.manifest</code>{' '}
          contents.
        </p>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Package className="h-4 w-4 text-accent" />
              Catalog
            </span>
          }
          description={bundlesCountLabel(list)}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">Bundle</th>
                <th className="px-4 py-2 font-medium">Version</th>
                <th className="px-4 py-2 font-medium">Revision</th>
                <th className="px-4 py-2 font-medium">Rego</th>
                <th className="px-4 py-2 font-medium">Data</th>
                <th className="px-4 py-2 font-medium">Roots</th>
                <th className="px-4 py-2 font-medium">Size</th>
                <th className="px-4 py-2 font-medium">Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {listLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={8}>
                    Loading…
                  </td>
                </tr>
              )}
              {listError && (
                <tr>
                  <td className="px-4 py-3 text-danger" colSpan={8}>
                    {String(listError)}
                  </td>
                </tr>
              )}
              {list?.bundles.length === 0 && !listLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={8}>
                    No bundles uploaded yet. Push a{' '}
                    <code className="orb-code">bundle.tar.gz</code> with{' '}
                    <code className="orb-code">
                      curl --upload-file bundle.tar.gz {'<host>'}/opa/{projectSlug}/{repo.slug}/api/bundles?name={'<name>'}&amp;version={'<v>'}
                    </code>{' '}
                    to seed the repo.
                  </td>
                </tr>
              )}
              {list?.bundles.map((b) => {
                const isSelected =
                  selected?.name === b.name && selected?.version === b.version;
                return (
                  <tr
                    key={`${b.name}@${b.version}`}
                    className={`border-b border-border/50 cursor-pointer ${
                      isSelected ? 'bg-bg-hover/60' : 'hover:bg-bg-hover/40'
                    }`}
                    onClick={() => setSelected(b)}
                  >
                    <td className="px-4 py-2 font-medium text-fg inline-flex items-center gap-2">
                      {b.parsed_ok ? (
                        <ShieldCheck className="h-3.5 w-3.5 text-accent" />
                      ) : (
                        <ShieldAlert className="h-3.5 w-3.5 text-warning" />
                      )}
                      {b.name}
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone="accent">{b.version}</Badge>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-fg-muted">
                      {b.revision ? shortHex(b.revision) : '—'}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {b.rego_files}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {b.data_paths_count}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {b.roots_count}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {bytes(b.size_bytes)}
                    </td>
                    <td className="px-4 py-2 text-fg-muted">
                      {new Date(b.uploaded_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {list && (
        <Card>
          <CardHeader title="OPA-server config snippet" />
          <CardBody>
            <PullSnippet hint={list.pull_hint} />
          </CardBody>
        </Card>
      )}

      {selected && (
        <BundleDetailCard
          projectSlug={projectSlug}
          repoSlug={repo.slug}
          name={selected.name}
          version={selected.version}
        />
      )}
    </div>
  );
}

interface PullSnippetProps {
  readonly hint: string;
}

function PullSnippet({ hint }: PullSnippetProps) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(hint);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail in non-secure contexts; the operator can
      // always select the snippet manually so we don't surface an error.
    }
  };
  return (
    <div className="flex items-start gap-3">
      <pre className="flex-1 bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre">
        {hint}
      </pre>
      <Button variant="ghost" type="button" onClick={onCopy} title="Copy snippet">
        <Copy className="h-4 w-4" />
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

interface BundleDetailCardProps {
  readonly projectSlug: string | undefined;
  readonly repoSlug: string | undefined;
  readonly name: string;
  readonly version: string;
}

function BundleDetailCard({
  projectSlug,
  repoSlug,
  name,
  version,
}: BundleDetailCardProps) {
  const { data, error, isLoading } = useOPABundleDetail(
    projectSlug,
    repoSlug,
    name,
    version,
  );
  const [tab, setTab] = useState<TabId>('overview');

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Package className="h-4 w-4 text-accent" />
            {name}
            <Badge tone="accent">{version}</Badge>
          </span>
        }
        description="Bundle contents — parsed from the archive on read."
      />
      <CardBody>
        {isLoading && <p className="text-fg-muted">Loading bundle…</p>}
        {error && <p className="text-danger">{String(error)}</p>}
        {data && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 border-b border-border pb-2">
              <TabButton
                id="overview"
                active={tab}
                onClick={setTab}
                icon={BookOpen}
                label="Overview"
              />
              <TabButton
                id="rego"
                active={tab}
                onClick={setTab}
                icon={FileCode}
                label={`Rego files (${data.rego.length})`}
              />
              <TabButton
                id="data"
                active={tab}
                onClick={setTab}
                icon={Database}
                label={`Data (${data.data.length})`}
              />
              <TabButton
                id="manifest"
                active={tab}
                onClick={setTab}
                icon={ScrollText}
                label="Manifest"
              />
            </div>
            {tab === 'overview' && <OverviewTab data={data} />}
            {tab === 'rego' && <RegoTab files={data.rego} />}
            {tab === 'data' && <DataTab docs={data.data} />}
            {tab === 'manifest' && <ManifestTab raw={data.manifest_raw} />}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

interface TabButtonProps {
  readonly id: TabId;
  readonly active: TabId;
  readonly onClick: (id: TabId) => void;
  readonly icon: typeof BookOpen;
  readonly label: string;
}

function TabButton({ id, active, onClick, icon: Icon, label }: TabButtonProps) {
  const isActive = id === active;
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
        isActive
          ? 'bg-accent/10 text-accent border border-accent/30'
          : 'text-fg-muted hover:text-fg hover:bg-bg-hover/50'
      }`}
      onClick={() => onClick(id)}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

interface OverviewTabProps {
  readonly data: NonNullable<ReturnType<typeof useOPABundleDetail>['data']>;
}

function OverviewTab({ data }: OverviewTabProps) {
  const meta = data.metadata ?? {};
  const roots = meta.roots ?? [];
  const dataPaths = meta.data_paths ?? [];
  const parsedOK = meta.parsed_ok !== false;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">Digest</dt>
          <dd className="font-mono text-xs text-fg break-all">{data.digest}</dd>
        </div>
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">Bundle size</dt>
          <dd className="tabular-nums text-fg">{bytes(data.size_bytes)}</dd>
        </div>
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">Revision</dt>
          <dd className="font-mono text-xs text-fg break-all">
            {meta.revision ? String(meta.revision) : <span className="text-fg-muted">—</span>}
          </dd>
        </div>
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">Manifest parse</dt>
          <dd>
            {parsedOK ? (
              <Badge tone="accent">OK</Badge>
            ) : (
              <Badge tone="warning">Malformed .manifest</Badge>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">Uploaded</dt>
          <dd className="text-fg-muted">{new Date(data.uploaded_at).toLocaleString()}</dd>
        </div>
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">File counts</dt>
          <dd className="text-fg">
            {data.rego.length} rego · {data.data.length} data
          </dd>
        </div>
      </dl>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold text-fg mb-2">Roots ({roots.length})</h3>
          {roots.length === 0 ? (
            <p className="text-fg-muted text-sm">
              No <code className="orb-code">roots</code> declared. OPA accepts
              this; the bundle owns no specific data prefix.
            </p>
          ) : (
            <ul className="space-y-1">
              {roots.map((r) => (
                <li key={r} className="font-mono text-xs text-fg-muted">
                  {r}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="text-sm font-semibold text-fg mb-2">
            Data paths ({dataPaths.length})
          </h3>
          {dataPaths.length === 0 ? (
            <p className="text-fg-muted text-sm">No data documents in bundle.</p>
          ) : (
            <ul className="space-y-1">
              {dataPaths.map((p) => (
                <li key={p} className="font-mono text-xs text-fg-muted">
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-fg mb-2">Pull URL</h3>
        <PullSnippet hint={data.pull_url} />
      </div>
    </div>
  );
}

interface RegoTabProps {
  readonly files: OPARegoFile[];
}

function RegoTab({ files }: RegoTabProps) {
  const [selectedPath, setSelectedPath] = useState<string | null>(
    files.length > 0 ? files[0].path : null,
  );
  const selected = files.find((f) => f.path === selectedPath) ?? null;

  if (files.length === 0) {
    return (
      <p className="text-fg-muted text-sm">
        No <code className="orb-code">.rego</code> files in bundle.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="md:col-span-1 border border-border rounded-md overflow-hidden">
        <ul className="divide-y divide-border">
          {files.map((f) => {
            const isActive = f.path === selectedPath;
            return (
              <li key={f.path}>
                <div
                  role="button"
                  tabIndex={0}
                  className={`w-full text-left px-3 py-2 text-xs font-mono transition-colors cursor-pointer focus:outline-none ${
                    isActive ? 'bg-accent/10 text-accent' : 'text-fg-muted hover:bg-bg-hover/50 hover:text-fg'
                  }`}
                  onClick={() => setSelectedPath(f.path)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedPath(f.path);
                    }
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate" title={f.path}>{f.path}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      <span className="text-[10px] tabular-nums text-fg-muted">
                        {bytes(f.size)}
                      </span>
                      <span onClick={(e) => e.stopPropagation()}>
                        <CopyIconButton value={f.path} label="Copy full path" />
                      </span>
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="md:col-span-2">
        {selected && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-fg-muted">{selected.path}</span>
              {selected.truncated && (
                <Badge tone="warning">Preview truncated</Badge>
              )}
            </div>
            <pre className="bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre max-h-[480px]">
              {selected.body}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

interface DataTabProps {
  readonly docs: OPADataDoc[];
}

function DataTab({ docs }: DataTabProps) {
  const [selectedPath, setSelectedPath] = useState<string | null>(
    docs.length > 0 ? docs[0].path : null,
  );
  const selected = docs.find((d) => d.path === selectedPath) ?? null;
  if (docs.length === 0) {
    return (
      <p className="text-fg-muted text-sm">
        No <code className="orb-code">data.json</code> /{' '}
        <code className="orb-code">data.yaml</code> documents in bundle.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="md:col-span-1 border border-border rounded-md overflow-hidden">
        <ul className="divide-y divide-border">
          {docs.map((d) => {
            const isActive = d.path === selectedPath;
            return (
              <li key={d.path}>
                <div
                  role="button"
                  tabIndex={0}
                  className={`w-full text-left px-3 py-2 text-xs font-mono transition-colors cursor-pointer focus:outline-none ${
                    isActive ? 'bg-accent/10 text-accent' : 'text-fg-muted hover:bg-bg-hover/50 hover:text-fg'
                  }`}
                  onClick={() => setSelectedPath(d.path)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedPath(d.path);
                    }
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate" title={d.path}>{d.path}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      <span className="text-[10px] tabular-nums text-fg-muted">
                        {bytes(d.size)}
                      </span>
                      <span onClick={(e) => e.stopPropagation()}>
                        <CopyIconButton value={d.path} label="Copy full path" />
                      </span>
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="md:col-span-2">
        {selected && (
          <div className="space-y-2">
            <span className="font-mono text-xs text-fg-muted">{selected.path}</span>
            <pre className="bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre max-h-[480px]">
              {prettyJSON(selected.body)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

interface ManifestTabProps {
  readonly raw: string;
}

function ManifestTab({ raw }: ManifestTabProps) {
  if (!raw) {
    return (
      <p className="text-fg-muted text-sm inline-flex items-center gap-2">
        <FileText className="h-4 w-4" />
        No <code className="orb-code">.manifest</code> in this bundle. OPA
        accepts bundles without a manifest; the bundle's revision is then
        unidentified.
      </p>
    );
  }
  return (
    <pre className="bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre max-h-[480px]">
      {prettyJSON(raw)}
    </pre>
  );
}

function prettyJSON(s: string): string {
  if (!s) return s;
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
